<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import segueDashboardAPI from 'dashboard/api/segueDashboard';
import PeriodFilter from './components/PeriodFilter.vue';
import ConversaModal from './components/ConversaModal.vue';

// v90: Equipe por TIME. v93 (feedback Kairo):
//  - CARDS de times com cores diferentes (clica no card -> abre o painel)
//  - papel na frente do nome: "Fulana (Agente)" / "(Supervisor)"
//  - TODAS as categorias de status por agente, com números CLICÁVEIS ->
//    janela com os clientes (banco, nome, cpf, telefone, datas, valor) e
//    botão WhatsApp (conversa estilo iPhone com ABRIR CONVERSA)
const store = useStore();
const teams = useMapGetter('teams/getTeams');

const teamId = ref(null);
const period = ref('today');
const customFrom = ref(null);
const customTo = ref(null);
const isLoading = ref(false);
const error = ref(null);
const data = ref(null);

// paleta dos cards de times (cicla)
const TEAM_COLORS = [
  { border: 'border-n-teal-9', bg: 'bg-n-teal-3', text: 'text-n-teal-11' },
  { border: 'border-n-blue-9', bg: 'bg-n-blue-3', text: 'text-n-blue-11' },
  { border: 'border-n-amber-9', bg: 'bg-n-amber-2', text: 'text-n-amber-11' },
  { border: 'border-n-iris-9', bg: 'bg-n-iris-3', text: 'text-n-iris-11' },
  { border: 'border-n-ruby-9', bg: 'bg-n-ruby-2', text: 'text-n-ruby-11' },
];
const teamColor = i => TEAM_COLORS[i % TEAM_COLORS.length];

const CATEGORIAS_COLS = [
  { key: 'Pagos', label: 'Pagos' },
  { key: 'Aguardando Pagamento', label: 'Ag. Pagto' },
  { key: 'Aguardando Assinatura', label: 'Ag. Assin' },
  { key: 'Em Análise', label: 'Em Análise' },
  { key: 'Cancelados', label: 'Cancelados' },
  { key: 'Reprovados', label: 'Reprovados' },
];

const load = async () => {
  if (!teamId.value) return;
  isLoading.value = true;
  error.value = null;
  try {
    const { data: resp } = await segueDashboardAPI.fetchEquipe({
      teamId: teamId.value,
      period: period.value,
      from: customFrom.value,
      to: customTo.value,
    });
    if (resp.error) error.value = resp.error;
    else data.value = resp;
  } catch (e) {
    error.value = e.message || 'Falha ao carregar equipe';
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  store.dispatch('teams/get');
});

const selecionarTime = t => {
  teamId.value = t.id;
  data.value = null;
  load();
};

const onPeriodChange = params => {
  period.value = params.period;
  customFrom.value = params.from || null;
  customTo.value = params.to || null;
  load();
};

const brl = v =>
  (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const statusDot = s => {
  if (s === 'online') return 'bg-n-teal-9';
  if (s === 'busy') return 'bg-n-amber-9';
  return 'bg-n-slate-8';
};

const initials = nome =>
  (nome || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();

const agentes = computed(() => data.value?.agentes || []);
const timeSelecionado = computed(() =>
  (teams.value || []).find(t => t.id === teamId.value)
);

// ---------------- drill-down: contratos do agente na categoria ------------
const detalhe = ref(null);
const detalheLoading = ref(false);
const detalheTitulo = ref('');
const conversaPhone = ref(null);
const conversaNome = ref('');

const abrirDetalhe = async (agente, categoria, label) => {
  detalheLoading.value = true;
  detalheTitulo.value = `${agente.nome} · ${label || 'Todos'}`;
  detalhe.value = { contratos: [] };
  try {
    const { data: resp } =
      await segueDashboardAPI.fetchContratosVendedorDetalhe({
        vendedorId: agente.id,
        categoria: categoria || '__total__',
        period: period.value,
        from: customFrom.value,
        to: customTo.value,
      });
    detalhe.value = resp;
  } catch (e) {
    detalhe.value = { error: e.message };
  } finally {
    detalheLoading.value = false;
  }
};

const abrirWhats = ct => {
  conversaPhone.value = ct.telefone || '';
  conversaNome.value = ct.nome || '';
};

const fecharTudo = () => {
  conversaPhone.value = null;
  detalhe.value = null;
};

const fmtDate = v => {
  if (!v) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v);
};

const bankChip = b =>
  b === 'FACTA' ? 'bg-n-amber-3 text-n-amber-11' : 'bg-n-blue-3 text-n-blue-11';
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="overflow-auto p-4 w-full h-full bg-n-background">
    <div class="flex flex-col gap-5 w-full">
      <!-- Header -->
      <div class="flex flex-wrap gap-4 justify-between items-start">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">Equipe</h1>
          <p class="mt-1 text-sm text-n-slate-11">
            Escolha um time para ver a performance dos agentes no período.
          </p>
        </div>
        <div class="flex flex-wrap gap-3 items-center">
          <PeriodFilter v-model="period" @change="onPeriodChange" />
          <button
            type="button"
            class="flex gap-1.5 items-center px-4 h-9 text-sm font-semibold text-white rounded-lg shadow-sm bg-n-brand hover:opacity-90"
            :disabled="!teamId"
            @click="load"
          >
            <span class="i-lucide-refresh-cw size-4" />
            Atualizar
          </button>
        </div>
      </div>

      <p v-if="error" class="text-sm text-n-ruby-11">{{ error }}</p>
      <p v-if="!teams.length" class="text-sm text-n-slate-11">
        Nenhum time cadastrado ainda — crie times em Configurações → Times.
      </p>

      <!-- v93: CARDS dos times (cores diferentes; clica pra abrir) -->
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <button
          v-for="(t, i) in teams"
          :key="t.id"
          type="button"
          class="p-4 text-left rounded-xl border-l-4 border transition-shadow"
          :class="[
            teamColor(i).border,
            teamId === t.id
              ? `${teamColor(i).bg} shadow-lg ring-1 ring-current ${teamColor(i).text}`
              : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-2',
          ]"
          @click="selecionarTime(t)"
        >
          <span
            class="text-[11px] font-bold uppercase tracking-wide"
            :class="teamColor(i).text"
          >
            Time
          </span>
          <p class="mt-0.5 text-base font-bold truncate text-n-slate-12">
            {{ t.name }}
          </p>
        </button>
      </div>

      <template v-if="teamId && data">
        <!-- Card do time selecionado -->
        <div class="p-4 rounded-xl border border-n-weak bg-n-solid-1">
          <div class="flex flex-wrap gap-6 items-center">
            <div>
              <span class="text-xs font-medium uppercase text-n-slate-11">Time</span>
              <p class="text-lg font-semibold text-n-slate-12">
                {{ data.team.name }}
              </p>
              <p class="text-sm text-n-slate-11">{{ data.team.agentes }} agente(s)</p>
            </div>
            <div class="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-5">
              <div>
                <span class="text-xs font-medium uppercase text-n-slate-11">Conversas</span>
                <p class="text-xl font-bold tabular-nums text-n-slate-12">
                  {{ data.totais.conversas }}
                </p>
              </div>
              <div>
                <span class="text-xs font-medium uppercase text-n-slate-11">
                  Clientes c/ oferta
                </span>
                <p class="text-xl font-bold tabular-nums text-n-slate-12">
                  {{ data.totais.clientes_oferta }}
                </p>
              </div>
              <div>
                <span class="text-xs font-medium uppercase text-n-slate-11">Digitados</span>
                <p class="text-xl font-bold tabular-nums text-n-slate-12">
                  {{ data.totais.digitados }}
                </p>
              </div>
              <div>
                <span class="text-xs font-medium uppercase text-n-slate-11">Pagos</span>
                <p class="text-xl font-bold tabular-nums text-n-teal-11">
                  {{ data.totais.pagos }}
                </p>
              </div>
              <div>
                <span class="text-xs font-medium uppercase text-n-slate-11">Valor pago</span>
                <p class="text-xl font-bold tabular-nums text-n-teal-11">
                  {{ brl(data.totais.valor_pago) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabela de agentes com TODAS as categorias -->
        <div class="overflow-x-auto rounded-xl border border-n-weak">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left bg-n-solid-2 text-n-slate-11">
                <th class="px-3 py-2.5 font-medium">Agente</th>
                <th class="px-3 py-2.5 font-medium text-right">Conversas</th>
                <th class="px-3 py-2.5 font-medium text-right">Abertas</th>
                <th class="px-3 py-2.5 font-medium text-right">C/ oferta</th>
                <th
                  v-for="c in CATEGORIAS_COLS"
                  :key="c.key"
                  class="px-3 py-2.5 font-medium text-right"
                >
                  {{ c.label }}
                </th>
                <th class="px-3 py-2.5 font-medium text-right">Valor pago</th>
              </tr>
            </thead>
            <tbody v-if="isLoading">
              <tr>
                <td :colspan="CATEGORIAS_COLS.length + 5" class="px-3 py-6 text-center text-n-slate-11">
                  Carregando…
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr
                v-for="a in agentes"
                :key="a.id"
                class="border-t border-n-weak hover:bg-n-alpha-1"
              >
                <td class="px-3 py-2.5">
                  <div class="flex gap-2.5 items-center">
                    <span class="relative">
                      <span class="grid place-items-center text-xs font-bold rounded-full size-8 bg-n-alpha-3 text-n-slate-12">
                        {{ initials(a.nome) }}
                      </span>
                      <span
                        class="absolute -right-0.5 -bottom-0.5 rounded-full border-2 size-2.5 border-n-solid-1"
                        :class="statusDot(a.availability)"
                      />
                    </span>
                    <div>
                      <p class="font-medium text-n-slate-12">
                        {{ a.nome }}
                        <span
                          class="ml-1 text-[10px] font-bold uppercase"
                          :class="a.papel === 'Supervisor' ? 'text-n-amber-11' : 'text-n-slate-11'"
                        >
                          ({{ a.papel }})
                        </span>
                      </p>
                      <p class="text-xs text-n-slate-11">{{ a.email }}</p>
                    </div>
                  </div>
                </td>
                <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-12">
                  {{ a.conversas }}
                </td>
                <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-12">
                  {{ a.abertas }}
                </td>
                <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-12">
                  {{ a.clientes_oferta }}
                </td>
                <td
                  v-for="c in CATEGORIAS_COLS"
                  :key="c.key"
                  class="px-3 py-2.5 text-right tabular-nums"
                >
                  <button
                    v-if="a.cats?.[c.key]?.qtd"
                    type="button"
                    class="font-semibold underline hover:opacity-75"
                    :class="c.key === 'Pagos' ? 'text-n-teal-11' : 'text-n-slate-12'"
                    @click="abrirDetalhe(a, c.key, c.label)"
                  >
                    {{ a.cats[c.key].qtd }}
                  </button>
                  <span v-else class="text-n-slate-11">—</span>
                </td>
                <td class="px-3 py-2.5 font-semibold text-right tabular-nums text-n-teal-11">
                  {{ brl(a.valor_pago) }}
                </td>
              </tr>
              <tr v-if="!agentes.length && data">
                <td :colspan="CATEGORIAS_COLS.length + 5" class="px-3 py-6 text-center text-n-slate-11">
                  Nenhum agente neste time
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <p v-else-if="teams.length && !teamId" class="py-6 text-sm text-center text-n-slate-11">
        👆 Selecione um time acima para ver a performance.
      </p>
    </div>

    <!-- Janela de detalhe (contratos do agente na categoria) -->
    <div
      v-if="detalhe"
      class="flex fixed inset-0 z-40 justify-center items-center p-4 bg-black/60"
      @click.self="detalhe = null"
    >
      <div class="flex flex-col w-full max-w-4xl max-h-[85vh] rounded-xl border shadow-xl border-n-weak bg-n-background">
        <div class="flex justify-between items-center px-4 py-3 border-b border-n-weak">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ detalheTitulo }}
            <span v-if="detalhe.total !== undefined" class="font-normal text-n-slate-11">
              · {{ detalhe.total }} contrato(s)
            </span>
          </h3>
          <button
            type="button"
            class="px-3 py-1 text-xs font-bold text-white bg-red-500 rounded-lg hover:bg-red-600"
            @click="detalhe = null"
          >
            ✕ Fechar
          </button>
        </div>
        <div class="overflow-auto p-4">
          <p v-if="detalheLoading" class="text-sm text-n-slate-11">Carregando…</p>
          <p v-else-if="detalhe.error" class="text-sm text-n-ruby-11">{{ detalhe.error }}</p>
          <table v-else class="w-full text-sm">
            <thead>
              <tr class="text-left bg-n-solid-2 text-n-slate-11">
                <th class="px-3 py-2 font-medium">Banco</th>
                <th class="px-3 py-2 font-medium">Nome</th>
                <th class="px-3 py-2 font-medium">CPF</th>
                <th class="px-3 py-2 font-medium">Telefone</th>
                <th class="px-3 py-2 font-medium">Digitação</th>
                <th class="px-3 py-2 font-medium">Pagamento</th>
                <th class="px-3 py-2 font-medium text-right">Valor</th>
                <th class="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(ct, i) in detalhe.contratos"
                :key="i"
                class="border-t border-n-weak hover:bg-n-alpha-1"
              >
                <td class="px-3 py-2">
                  <span class="px-2 py-0.5 text-xs font-semibold rounded" :class="bankChip(ct.banco)">
                    {{ ct.banco }}
                  </span>
                </td>
                <td class="px-3 py-2 text-n-slate-12">{{ ct.nome }}</td>
                <td class="px-3 py-2 tabular-nums text-n-slate-12">{{ ct.cpf }}</td>
                <td class="px-3 py-2 text-n-slate-12">{{ ct.telefone }}</td>
                <td class="px-3 py-2 tabular-nums text-n-slate-12">{{ fmtDate(ct.data_digitacao) }}</td>
                <td class="px-3 py-2 tabular-nums text-n-slate-12">{{ fmtDate(ct.data_pagamento) }}</td>
                <td class="px-3 py-2 font-semibold text-right tabular-nums text-n-slate-12">
                  {{ brl(ct.valor) }}
                </td>
                <td class="px-3 py-2 text-right">
                  <button
                    v-if="ct.telefone"
                    type="button"
                    class="inline-flex justify-center items-center text-white rounded-lg size-7 bg-n-teal-9 hover:opacity-90"
                    title="Ver conversa"
                    @click="abrirWhats(ct)"
                  >
                    <span class="i-lucide-message-circle size-4" />
                  </button>
                </td>
              </tr>
              <tr v-if="!detalhe.contratos?.length">
                <td colspan="8" class="px-3 py-6 text-center text-n-slate-11">
                  Nenhum contrato
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Conversa estilo iPhone -->
    <ConversaModal
      v-if="conversaPhone"
      :phone="conversaPhone"
      :nome="conversaNome"
      @close="conversaPhone = null"
      @navigate="fecharTudo"
    />
  </div>
</template>
